import { parseBuildTrim } from '@/components/organisms/ChooseTrimForm';
import { formatRange } from '@/lib/formatRange';
import { components } from '@/types/be-schema';
import { parseVehicleTrim } from './parseVehicleTrim';
import {
  RawTrimDetails,
  RawTrimSpecs,
  TrimSpecs,
  TrimStyle,
  TrimStyleWithSpecs,
} from './schema/build/types';
import { HighlightsBadge, SortedHighlightsBadges } from './schema/shared/types';

export const parseModelLevelHighlights = (
  raw: components['schemas']['ModelLevelVehicle']
): SortedHighlightsBadges | null => {
  const highlightBadges: SortedHighlightsBadges = {
    primary: <HighlightsBadge[]>[],
    secondary: <HighlightsBadge[]>[],
  };

  const range = formatRange(
    raw.MinRange?.Value,
    raw.MaxRange?.Value,
    raw.MaxRange?.Unit
  );

  if (range) {
    highlightBadges.primary.push({
      name: 'Est. Range',
      description: range,
    });
  }
  const batterySize = raw.MaxBatteryCapacity;
  if (batterySize) {
    highlightBadges.primary.push({
      name: 'Battery capacity',
      description: `${batterySize} kWh`,
    });
  }
  const fastCharger = raw.MinDCFastCharging || raw.MaxDCFastCharging;
  if (fastCharger) {
    highlightBadges.primary.push({
      name: 'Fast charger',
      description: `${fastCharger} min`,
    });
  }

  const rawPrimaryFeatureList = ['MAX_SEATS', 'DRIVETRAIN'];

  raw?.Features?.forEach((feature) => {
    if (rawPrimaryFeatureList.includes(feature.Name)) {
      const name = feature.Name === 'MAX_SEATS' ? 'Seats' : 'Drivetrain';
      highlightBadges.primary.push({
        name,
        description: String(feature.Value),
      });
    }
  });

  const wheelSize = raw.MaxWheelSize;
  if (wheelSize) {
    highlightBadges.secondary.push({
      name: 'Wheel size',
      description: `${wheelSize} inches`,
    });
  }
  const rawBatteryWarranty = raw.BatteryWarranty;
  if (rawBatteryWarranty) {
    highlightBadges.secondary.push({
      name: 'Battery warranty',
      description: rawBatteryWarranty,
    });
  }

  if (
    highlightBadges.primary.length === 0 &&
    highlightBadges.secondary.length === 0
  ) {
    return null;
  }

  return highlightBadges;
};

export const fetchModelLevelTrims = async ({
  make,
  year,
  model,
  fromClient = false,
}: {
  make: string;
  year: string;
  model: string;
  fromClient?: boolean;
}): Promise<TrimStyleWithSpecs[]> => {
  try {
    const requestUrl = fromClient
      ? `/api/trim/${year}/${make}/${model}`
      : `${process.env.API_ENDPOINT}/api/model_level_page/model_level_vehicle/${year}/${make}/${model}/trims`;

    const trims: TrimStyle[] = await fetch(requestUrl)
      .then((r) => r.json())
      .then(({ Styles }: { Styles: RawTrimDetails[] }) =>
        Styles.map((t: RawTrimDetails) => {
          const parsed = parseBuildTrim(t);
          return {
            ...parsed,
            range:
              parsed.range || parsed.rangeAllElectric || parsed.rangeHybrid,
          };
        })
      );

    const extendedTrim: TrimStyleWithSpecs[] = await Promise.all(
      trims.map((trim) =>
        fetch(
          `${
            fromClient ? '' : process.env.API_ENDPOINT
          }/api/compare/getVehicleStylesData/?year=${year}&make=${make}&model=${model}&trim=${encodeURIComponent(
            trim.trim
          )}`
        )
          .then((r) => r.json() as Promise<RawTrimSpecs>)
          .then((specs) => {
            const parsed: TrimSpecs = parseVehicleTrim(specs);
            return parsed;
          })
          .then((allSpecs) => {
            return {
              ...trim,
              specs: allSpecs,
              image: trim.exteriorImages?.[0].images?.[0]?.href,
            };
          })
      )
    );
    return extendedTrim;
  } catch (_) {
    return [];
  }
};

export const getAllMakes = async (): Promise<string[]> => {
  try {
    const res = await fetch(`${process.env.API_ENDPOINT}/api/build/makes`);
    if (!res.ok) {
      return [];
    }
    const data = await res.json();
    return data.Makes || [];
  } catch (_) {
    return [];
  }
};

export const getUsedCarIncentivesTaxCredit = (salePrice: number): string =>
  salePrice <= 25000 ? (4000).toLocaleString() : '';

export const generateVehicleDetailQuery = (
  make: string,
  model: string,
  year: number | string,
  listingId = ''
): string => {
  return [make, model.replaceAll(' ', '-'), year, listingId]
    .join('-')
    .toLowerCase();
};
